import { disableMouseLock, enableMouseLock } from '../core/Controls';
import AssetManager from '../managers/AssetManager';
import EventBus from '../EventBus';
import { isTouchDevice } from './MobileControls';

type GunChangedHandler = (gun: string) => void;

let keydownHandler: any | null = null;
let cleanupFns: Array<() => void> = [];

function init(gunChangedHandler: GunChangedHandler) {
  let container = document.getElementById('ui-container');
  if (!container) {
    container = document.createElement('div');
    container.id = 'ui-container';
    container.style.position = 'fixed';
    container.style.top = '0';
    container.style.left = '0';
    container.style.width = '100%';
    container.style.height = '100%';
    container.style.pointerEvents = 'none';
    container.style.zIndex = '1000';
    document.body.appendChild(container);
  }
  const mobile = isTouchDevice();
  if (mobile) {
    container.classList.add('ui-mobile');
  }
  const gunSelector = createGunSelector(gunChangedHandler);
  container.appendChild(gunSelector);
  const volumeControl = createVolumeControl();
  container.appendChild(volumeControl);
  // const infoBox = createInfoBox();
  // container.appendChild(infoBox);
  // Key bindings for weapon selection (Digit1..Digit5 and Czech keys +ěščř)
  keydownHandler = (e: KeyboardEvent) => {
    const code = (e.code || '').toLowerCase();
    const key = (e.key || '').toLowerCase();
    const gunSelectorEl = document.getElementById('gunSelector');
    if (!gunSelectorEl) return;
    const buttons = Array.from(gunSelectorEl.querySelectorAll('.gun-button')) as HTMLButtonElement[];
    const selectIndex = (idx: number) => {
      if (idx < 0 || idx >= buttons.length) return;
      buttons.forEach((b) => b.classList.remove('selected'));
      const btn = buttons[idx];
      btn.classList.add('selected');
      const gun = btn.getAttribute('data-gun') || '';
      if (gun) {
        gunChangedHandler(gun);
        EventBus.emit('weapon:change', gun);
      }
    };
    const digitMap: Record<string, number> = {
      'digit1': 0,
      'digit2': 1,
      'digit3': 2,
      'digit4': 3,
      'digit5': 4,
    };
    if (code in digitMap) {
      selectIndex(digitMap[code]);
      return;
    }
    const czechMap: Record<string, number> = {
      '+': 0,
      'ě': 1,
      'š': 2,
      'č': 3,
      'ř': 4,
    };
    if (key in czechMap) {
      selectIndex(czechMap[key]);
    }
  };
  window.addEventListener('keydown', keydownHandler);
  cleanupFns.push(() => window.removeEventListener('keydown', keydownHandler as any));

  if (mobile) {
    disableMouseLock();
  } else {
    enableMouseLock();
  }
}

function createGunSelector(gunChangedHandler: GunChangedHandler) {
  const gunSelector = document.createElement('div');
  gunSelector.id = 'gunSelector';
  gunSelector.classList.add('gun-selector');
  const guns = ['rocket', 'shield', 'magic_1'];
  const digitHints = ['1', '2', '3', '4', '5'];
  const czechHints = ['+', 'ě', 'š', 'č', 'ř'];
  guns.forEach((gun, index) => {
    const gunButton = document.createElement('button');
    gunButton.classList.add('gun-button');
    gunButton.setAttribute('data-gun', gun);
    const img = document.createElement('img');
    img.src = `../assets/textures/guns/${gun}.png`;
    img.alt = `Gun ${gun}`;
    const hint = document.createElement('span');
    hint.classList.add('gun-key-hint');
    const d = digitHints[index] || '';
    const cz = czechHints[index] || '';
    hint.textContent = d && cz ? `${d} / ${cz}` : d || cz;
    gunButton.appendChild(img);
    gunButton.appendChild(hint);
    gunButton.addEventListener('click', () => changeGun(gun, gunButton));
    gunSelector.appendChild(gunButton);
    if (index === 0) {
      gunButton.classList.add('selected');
      // Notify both handler and EventBus for default selection
      gunChangedHandler(gun);
      EventBus.emit('weapon:change', gun);
    }
  });
  function changeGun(gun: string, selectedButton: HTMLButtonElement) {
    const buttons = gunSelector.querySelectorAll('.gun-button');
    buttons.forEach((button) => button.classList.remove('selected'));
    selectedButton.classList.add('selected');
    gunChangedHandler(gun);
    EventBus.emit('weapon:change', gun);
  }
  return gunSelector;
}

function createVolumeControl() {
  const volumeContainer = document.createElement('div');
  volumeContainer.classList.add('volume-control');
  const label = document.createElement('label');
  label.textContent = 'Hlasitost:';
  label.htmlFor = 'volumeSlider';
  const slider = document.createElement('input');
  slider.type = 'range';
  slider.id = 'volumeSlider';
  slider.min = '0';
  slider.max = '1';
  slider.step = '0.01';
  const savedVolume = localStorage.getItem('gameVolume');
  slider.value = savedVolume !== null ? String(parseFloat(savedVolume)) : '0.5';
  slider.addEventListener('input', () => {
    if ((AssetManager.audioListener as any).gain) {
      (AssetManager.audioListener as any).gain.gain.value = parseFloat(slider.value);
    }
    localStorage.setItem('gameVolume', slider.value);
  });
  if ((AssetManager.audioListener as any).gain) {
    (AssetManager.audioListener as any).gain.gain.value = parseFloat(slider.value);
  }
  volumeContainer.appendChild(label);
  volumeContainer.appendChild(slider);
  return volumeContainer;
}

function createInfoBox() {
  const infoBox = document.createElement('div');
  infoBox.id = 'infoBox';
  const offlineBlock = document.createElement('div');
  offlineBlock.classList.add('infoBlock');
  offlineBlock.innerHTML = `
        <h2>OFFLINE</h2>
        <input type="text" id="usernameInput" placeholder="Jméno" oninput="updateUsername()">
        <p>Server Name: MyGameServer</p>
        <p>Connection Status: Waiting...</p>
        <p>Players Connected: 0/20</p>
    `;
  const activeServersBlock = document.createElement('div');
  activeServersBlock.classList.add('infoBlock');
  activeServersBlock.innerHTML = `
        <h2>Active Servers</h2>
        <p>Připraven</p>
        <p>Připojeno k: MyGameServer</p>
    `;
  infoBox.appendChild(offlineBlock);
  infoBox.appendChild(activeServersBlock);
  return infoBox;
}

let pauseMenu: HTMLDivElement | null = null;
let pauseMenuVisible = false;

function showPauseMenu(onSkinChange: (s: string) => void, onNameChange: (n: string) => void, initialSkin: string, initialName: string) {
  if (pauseMenuVisible) return;
  pauseMenuVisible = true;
  const overlay = document.createElement('div');
  overlay.id = 'pauseOverlay';
  overlay.style.position = 'fixed';
  overlay.style.top = '0';
  overlay.style.left = '0';
  overlay.style.width = '100%';
  overlay.style.height = '100%';
  overlay.style.background = 'rgba(0, 0, 0, 0.5)';
  overlay.style.zIndex = '1002';
  overlay.style.pointerEvents = 'auto';

  const menu = document.createElement('div');
  menu.id = 'pauseMenu';
  menu.style.position = 'absolute';
  menu.style.top = '50%';
  menu.style.left = '50%';
  menu.style.transform = 'translate(-50%, -50%)';
  menu.style.background = '#222';
  menu.style.padding = '20px';
  menu.style.color = '#fff';
  menu.style.borderRadius = '8px';

  const nameLabel = document.createElement('label');
  nameLabel.textContent = 'Name:';
  const nameInput = document.createElement('input');
  nameInput.type = 'text';
  nameInput.value = initialName || '';

  const skinLabel = document.createElement('label');
  skinLabel.textContent = 'Skin:';
  const skinSelect = document.createElement('select');
  const skins = ['T90A', 'T90B', 'T90C', 'T90D', 'T90E', 'T90F', 'T90G'];
  skins.forEach((s) => {
    const option = document.createElement('option');
    option.value = s;
    option.textContent = s;
    if (s === initialSkin) option.selected = true;
    skinSelect.appendChild(option);
  });

  const resumeButton = document.createElement('button');
  resumeButton.textContent = 'Resume';
  resumeButton.onclick = () => hidePauseMenu();

  const saveButton = document.createElement('button');
  saveButton.textContent = 'Save';
  saveButton.onclick = () => {
    onSkinChange(skinSelect.value);
    onNameChange(nameInput.value);
    setCookie('tankSkin', skinSelect.value, 365);
    setCookie('playerName', nameInput.value, 365);
  };

  const quitButton = document.createElement('button');
  quitButton.textContent = 'Quit';
  quitButton.onclick = () => hidePauseMenu();

  menu.appendChild(nameLabel);
  menu.appendChild(nameInput);
  menu.appendChild(skinLabel);
  menu.appendChild(skinSelect);
  menu.appendChild(resumeButton);
  menu.appendChild(saveButton);
  menu.appendChild(quitButton);
  overlay.appendChild(menu);
  document.body.appendChild(overlay);
}

function hidePauseMenu() {
  if (!pauseMenuVisible) return;
  const overlay = document.getElementById('pauseOverlay');
  if (overlay && overlay.parentNode) overlay.parentNode.removeChild(overlay);
  pauseMenuVisible = false;
}

function isPauseMenuVisible() {
  return pauseMenuVisible;
}

function setCookie(name: string, value: string, days: number) {
  let expires = '';
  if (days) {
    const date = new Date();
    date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
    expires = '; expires=' + date.toUTCString();
  }
  document.cookie = name + '=' + (value || '') + expires + '; path=/';
}

function getCookie(name: string) {
  const nameEQ = name + '=';
  const ca = document.cookie.split(';');
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) == ' ') c = c.substring(1, c.length);
    if (c.indexOf(nameEQ) == 0) return c.substring(nameEQ.length, c.length);
  }
  return null;
}

let healthBar: HTMLDivElement | null = null;

function createHealthBar(initialValue = 100, maxValue = 100) {
  let bar = document.createElement('div');
  bar.id = 'healthBarContainer';
  bar.style.position = 'fixed';
  bar.style.top = '24px';
  bar.style.left = '50%';
  bar.style.transform = 'translateX(-50%)';
  bar.style.width = '320px';
  bar.style.height = '28px';
  bar.style.background = '#222';
  bar.style.border = '2px solid #900';
  bar.style.borderRadius = '14px';
  bar.style.zIndex = '1001';
  bar.style.boxShadow = '0 2px 8px rgba(0,0,0,0.3)';

  let fill = document.createElement('div');
  fill.id = 'healthBarFill';
  fill.style.height = '100%';
  fill.style.width = (initialValue / maxValue) * 100 + '%';
  fill.style.background = 'linear-gradient(90deg, #e53935, #ffb300)';
  fill.style.borderRadius = '12px';
  fill.style.transition = 'width 0.2s';
  fill.style.position = 'absolute';
  fill.style.left = '0';
  fill.style.top = '0';
  fill.style.zIndex = '1';

  let label = document.createElement('span');
  label.id = 'healthBarLabel';
  label.style.position = 'absolute';
  label.style.left = '0';
  label.style.top = '0';
  label.style.width = '100%';
  label.style.height = '100%';
  label.style.textAlign = 'center';
  label.style.lineHeight = '28px';
  label.style.color = '#fff';
  label.style.fontWeight = 'bold';
  label.style.textShadow = '0 1px 2px #000, 0 0 8px #000';
  label.style.zIndex = '2';
  label.innerText = `${initialValue} / ${maxValue}`;

  bar.appendChild(fill);
  bar.appendChild(label);
  return bar;
}

function showHealthBar(initialValue = 100, maxValue = 100) {
  if (healthBar) return;
  healthBar = createHealthBar(initialValue, maxValue);
  document.body.appendChild(healthBar);
}

function updateHealthBar(current: number, max: number) {
  if (!healthBar) return;
  const fill = healthBar.querySelector('#healthBarFill') as HTMLDivElement;
  const label = healthBar.querySelector('#healthBarLabel') as HTMLSpanElement;
  fill.style.width = (current / max) * 100 + '%';
  label.innerText = `${current} / ${max}`;
}

function hideHealthBar() {
  if (healthBar && healthBar.parentNode) {
    healthBar.parentNode.removeChild(healthBar);
  }
  healthBar = null;
}

function dispose() {
  const container = document.getElementById('ui-container');
  if (!container) {
    console.error(`Container with id ui-container not found.`);
    return;
  }
  const gunSelector = document.getElementById('gunSelector');
  if (gunSelector) {
    const gunButtons = gunSelector.querySelectorAll('.gun-button');
    gunButtons.forEach((gunButton) => {
      gunButton.replaceWith(gunButton.cloneNode(true));
    });
    container.removeChild(gunSelector);
  }
  // Remove key listeners
  cleanupFns.forEach((fn) => {
    try { fn(); } catch {}
  });
  cleanupFns = [];
  const volumeControl = container.querySelector('.volume-control');
  if (volumeControl) container.removeChild(volumeControl);
  const infoBox = document.getElementById('infoBox');
  if (infoBox) container.removeChild(infoBox);
  disableMouseLock();
}

export const GameplayUI = {
  init,
  dispose,
  showPauseMenu,
  hidePauseMenu,
  isPauseMenuVisible,
  getCookie,
  showHealthBar,
  updateHealthBar,
  hideHealthBar,
};
